"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import styles from "../css/creditConsent.module.css";
import api from "../../../api";
import { validateMainMemberId } from "../../../api/profile/create_profile";

const DecisionResultModal = ({ variant, title, body, onClose }) => {
  const isApproved = variant === "approved";
  return (
    <Modal closeStyle="plain" onClose={onClose}>
      <div className={styles.decisionModal}>
        <div
          className={`${styles.decisionIcon} ${
            isApproved ? styles.decisionIconApproved : styles.decisionIconDenied
          }`}
          aria-hidden
        >
          {isApproved ? (
            <CheckCircle2 size={28} color="#059669" strokeWidth={2.5} />
          ) : (
            <XCircle size={28} color="#dc2626" strokeWidth={2.5} />
          )}
        </div>
        <h2 className={styles.decisionTitle}>{title}</h2>
        <p className={styles.decisionBody}>{body}</p>
      </div>
    </Modal>
  );
};

const CreditConsent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const creditFlow = location?.state?.creditFlow;
  const isReviewMode = creditFlow?.mode === "review";
  const creditStatus = String(creditFlow?.status || "").toLowerCase();
  const isDecisionStatus = creditStatus === "decision";
  const isReadOnly = isReviewMode && ["awaiting", "pending", "decision"].includes(creditStatus);
  const formData = location?.state?.formData || {};
  const steps = useMemo(
    () => [
      { key: "personal", label: "Personal" },
      { key: "contact", label: "Contact" },
      { key: "employment", label: "Employment" },
      { key: "financial", label: "Financial" },
      { key: "consent", label: "Consent" },
    ],
    []
  );

  const [submittedOpen, setSubmittedOpen] = useState(false);
  const [approvedOpen, setApprovedOpen] = useState(false);
  const [deniedOpen, setDeniedOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const applicationId = creditFlow?.applicationId;

  const buildPayload = () => {
    return {
      personal: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        idNumber: formData.idNumber,
        dob: formData.dob,
        gender: formData.gender,
      },
      contact: {
        phone: formData.mobile,
        email: formData.email,
        streetAddress: formData.streetAddress || "",
        suburb: formData.suburb || "",
        city: formData.city || "",
        postalCode: formData.postalCode || "",
        province: formData.province || "",
      },
      employment: {
        employmentStatus: formData.employmentStatus,
        employer: formData.employerName,
        incomeMonthly: formData.monthlyIncome,
        payFrequency: formData.payFrequency,
      },
      financial: {
        hasExistingCredit: formData.hasExistingCredit,
        preferredPaymentMethod: formData.preferredPaymentMethod,
        expenses: Array.isArray(formData.expenses) ? formData.expenses : [],
      },
      consent: {
        accepted: true,
        date: new Date().toISOString(),
      },
    };
  };

  const updateStatus = async (status) => {
    if (!applicationId) {
      alert("Missing applicationId");
      return false;
    }
    try {
      await api.patch(`/api/public/credit-applications/${applicationId}/status`, {
        status,
      });
      return true;
    } catch (err) {
      console.error("Status update failed:", err);
      alert(err?.response?.data?.error || err?.message || "Failed to update status");
      return false;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      const fd = new FormData();
      fd.append("personal", JSON.stringify(payload.personal));
      fd.append("contact", JSON.stringify(payload.contact));
      fd.append("employment", JSON.stringify(payload.employment));
      fd.append("financial", JSON.stringify(payload.financial));
      fd.append("consent", JSON.stringify(payload.consent));

      if (formData.idCopy instanceof File) fd.append("idCopy", formData.idCopy);
      if (formData.payslip instanceof File) fd.append("payslip", formData.payslip);
      if (formData.bankStatement instanceof File) fd.append("bankStatement", formData.bankStatement);

      await api.post("/api/public/credit-applications", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSubmittedOpen(true);
    } catch (err) {
      console.error("Submit failed:", err);
      alert(err?.message || "Failed to submit application");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <Navbar backTo="/" hideBack={isReviewMode} />

      <main className={styles.main}>
        <div className={styles.titleWrap}>
          <h1 className={styles.title}>Credit Application</h1>
          <p className={styles.subtitle}>
            Complete the form below to apply for purchase-on-credit at your pharmacy.
          </p>
        </div>

        <div className={styles.stepper} aria-label="Application steps">
          {steps.map((s, idx) => {
            const isActive = idx === 4;
            const isDone = idx < 4;
            return (
              <div key={s.key} className={styles.stepItem}>
                <div
                  className={`${styles.stepCircle} ${isActive ? styles.stepActive : ""} ${
                    isDone ? styles.stepDone : ""
                  }`}
                >
                  {isDone ? "✓" : idx + 1}
                </div>
                <div className={styles.stepLabel}>{s.label}</div>
                {idx < steps.length - 1 ? <div className={styles.stepLine} aria-hidden /> : null}
              </div>
            );
          })}
        </div>

        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon} aria-hidden>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 3h10v4H7V3z" stroke="currentColor" strokeWidth="2" />
                <path d="M6 7h12v14H6V7z" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className={styles.sectionTitle}>Consent &amp; Agreements</div>
          </div>

          <div className={styles.consentStack}>
            <div className={styles.consentRow}>
  
              <div className={styles.consentText}>
                I confirm that the information provided is accurate and complete to the best of my knowledge.
              </div>
            </div>
            <div className={styles.consentRow}>
              <div className={styles.consentText}>
                I consent to credit assessment and data processing in accordance with applicable regulations.
              </div>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.actions}>
            <Button
              variant="primary"
              type="button"
              onClick={() =>
                isReviewMode
                  ? navigate("/credits/financial", { state: { creditFlow, formData } })
                  : navigate(-1)
              }
            >
              Back
            </Button>
            {!isReviewMode ? (
              <Button
                variant="approve"
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Acknowledge and Submit"}
              </Button>
            ) : null}
          </div>
        </section>

        {isReviewMode && isDecisionStatus ? (
          <div className={styles.decisionActions}>
            <Button
              variant="approve"
              type="button"
              onClick={async () => {
                const idNumber = formData?.idNumber;
                let status = "pending";
                let patientExists = false;
                if (idNumber) {
                  try {
                    const validation = await validateMainMemberId(idNumber);
                    if (validation?.exists || validation?.patientId) {
                      status = "complete";
                      patientExists = true;
                    }
                  } catch (_) {
                    // If validation fails, default to pending
                  }
                }
                const ok = await updateStatus(status);
                if (!ok) return;
                
                // If patient exists and we're setting status to complete, update hasCredit flag
                if (patientExists && idNumber) {
                  try {
                    await api.post("/api/public/credit-applications/set-patient-hascredit", {
                      idNumber,
                    });
                  } catch (_) {
                    // Silently ignore - hasCredit update is not critical for approval
                  }
                }
                
                setApprovedOpen(true);
              }}
            >
              Approve
            </Button>
            <Button
              variant="cancel"
              type="button"
              onClick={async () => {
                const ok = await updateStatus("denied");
                if (!ok) return;
                setDeniedOpen(true);
              }}
            >
              Deny
            </Button>
          </div>
        ) : null}
      </main>

      <Footer />

      {!isReviewMode && submittedOpen && (
        <Modal closeStyle="plain" onClose={() => setSubmittedOpen(false)}>
          <div className={styles.submittedModal}>
            <div className={styles.submittedIcon} aria-hidden>
              <CheckCircle2 size={28} color="#059669" strokeWidth={2.5} />
            </div>
            <h2 className={styles.submittedTitle}>Application Submitted</h2>
            <p className={styles.submittedBody}>
              Your credit application has been received. We'll review your details and get back to you shortly.
            </p>
            <Button
              variant="primary"
              type="button"
              className={styles.submittedBtn}
              onClick={() => navigate("/")}
            >
              Back to Home
            </Button>
          </div>
        </Modal>
      )}

      {isReviewMode && isDecisionStatus && approvedOpen ? (
        <DecisionResultModal
          variant="approved"
          title="Application Approved"
          body="This applicant has been approved for purchase on credit."
          onClose={() => {
            setApprovedOpen(false);
            navigate("/credits/debtors");
          }}
        />
      ) : null}

      {isReviewMode && isDecisionStatus && deniedOpen ? (
        <DecisionResultModal
          variant="denied"
          title="Application Denied"
          body="This applicant was not approved for credit."
          onClose={() => {
            setDeniedOpen(false);
            navigate("/credits/debtors");
          }}
        />
      ) : null}
    </div>
  );
};

export default CreditConsent;
